import { SUPPLIER_BACKEND_API_ENDPOINTS } from "../config/apiConfig";
import { apiClient } from "./api-client.service";
import { authStorageService } from "./auth-storage.service";

import type {
  ChangePasswordLinkPayload,
  ChangePasswordLinkResponse,
  LoginPayload,
  LoginResponse,
  MenuAccessResponse,
  ResetPasswordPayload,
  SupplierInfo,
  UserInfo,
  UserProfileInfo,
} from "../types/authentication.types";

export const authenticationService = {
  async login(payload: LoginPayload): Promise<LoginResponse> {
    try {   
      const response = await apiClient<LoginResponse>(
        SUPPLIER_BACKEND_API_ENDPOINTS.authentication.login,
        {
          method: "POST",
          body: JSON.stringify(payload),
          cache: "no-store",
        },
      );

      if (!response.success) {
        return response;
      }

      const { token, user_info, user_profile_info, wallet_info, bank_details } =
        response.resData ?? {};
      
      if (!token) {
        return {
          ...response,
          success: false,
          message: "Authentication token was not returned by the API.",
        };
      }

      if (!user_info) {
        return {
          ...response,
          success: false,
          message: "User details were not returned by the API.",
        };
      }
      authStorageService.saveAuthentication(
        token,
        user_info,
        user_profile_info,
        wallet_info,
        bank_details,
        {
          supplier_id: user_profile_info?.user_id,
          supplier_name: user_profile_info?.agency_name,
          supplier_code: user_profile_info?.supplier_code,
          supplier_available_balance: wallet_info?.available_balance,
        },
      );

      return response;
    } catch (error) {
      return {
        success: false,
        statusCode: 500,
        message:
          error instanceof Error
            ? error.message
            : "Login failed due to an unexpected error.",
        resData: null as unknown as LoginResponse["resData"],
      };
    }
  },

  async getMenuAccess(
    roleCode: number | string,
    token?: string,
  ): Promise<MenuAccessResponse> {
    try {
      return await apiClient<MenuAccessResponse>(
        SUPPLIER_BACKEND_API_ENDPOINTS.authentication.menuAccess(roleCode),
        {
          method: "GET",
          isAuthenticated: true,
          ...(token ? { token } : {}),
          cache: "no-store",
        },
      );
    } catch (error) {
      return {
        success: false,
        statusCode: 500,
        message:
          error instanceof Error
            ? error.message
            : "Failed to load menu access due to an unexpected error.",
        resData: [],
      };
    }
  },

  async changePasswordLink(
    payload: ChangePasswordLinkPayload,
  ): Promise<ChangePasswordLinkResponse> {
    try {
      return await apiClient<ChangePasswordLinkResponse>(
        SUPPLIER_BACKEND_API_ENDPOINTS.authentication.changePasswordLink,
        {
          method: "POST",
          body: JSON.stringify(payload),
          cache: "no-store",
        },
      );
    } catch (error) {
      return {
        success: false,
        statusCode: 500,
        message:
          error instanceof Error
            ? error.message
            : "Failed to send reset link due to an unexpected error.",
        resData: null,
      };
    }
  },

  async resetPassword(
    payload: ResetPasswordPayload,
  ): Promise<ChangePasswordLinkResponse> {
    try {
      return await apiClient<ChangePasswordLinkResponse>(
        SUPPLIER_BACKEND_API_ENDPOINTS.authentication.changePasswordLink,
        {
          method: "POST",
          body: JSON.stringify(payload),
          cache: "no-store",
        },
      );
    } catch (error) {
      return {
        success: false,
        statusCode: 500,
        message:
          error instanceof Error
            ? error.message
            : "Failed to reset password due to an unexpected error.",
        resData: null,
      };
    }
  },

  logout(): void {
    try {
      authStorageService.clearAuthentication();

      if (typeof window !== "undefined") {
        window.location.replace("/");
      }
    } catch (error) {
      console.error("Failed to logout:", error);
    }
  },

  getToken(): string | null {
    try {
      return authStorageService.getToken();
    } catch (error) {
      console.error("Failed to get token:", error);
      return null;
    }
  },

  getCurrentUser(): UserInfo | null {
    try {
      return authStorageService.getUser();
    } catch (error) {
      console.error("Failed to get current user:", error);
      return null;
    }
  },

  getCurrentUserProfile(): UserProfileInfo | null {
    try {
      return authStorageService.getUserProfile();
    } catch (error) {
      console.error("Failed to get user profile:", error);
      return null;
    }
  },

  getSupplierDetail(): SupplierInfo | null {
    try {
      return authStorageService.getSupplierDetail();
    } catch (error) {
      console.error("Failed to get supplier data:", error);
      return null;
    }
  },

  isAuthenticated(): boolean {
    try {
      return authStorageService.isAuthenticated();
    } catch (error) {
      console.error("Failed to check authentication:", error);
      return false;
    }
  },
};
